import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { MONTHS, FITNESS } from "../../lib/adventureConstants";
import { Users, DoorClosed, CalendarDays, Activity } from "lucide-react";

export default function TripDetailsModal({ open, onOpenChange, trip, onSave, regionName }) {
  const [form, setForm] = useState(trip);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-md border-sand-dark" data-testid="trip-details-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Your trip to {regionName}
          </DialogTitle>
          <DialogDescription className="text-ink-secondary">
            A few details so we show only what's available and priced for you.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label className="font-mono text-xs uppercase tracking-widest text-moss flex items-center gap-1.5 mb-1.5">
              <CalendarDays className="w-3.5 h-3.5" /> Travel month
            </Label>
            <Select value={String(form.month)} onValueChange={(v) => set("month", Number(v))}>
              <SelectTrigger data-testid="trip-month" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-moss flex items-center gap-1.5 mb-1.5">
              <Users className="w-3.5 h-3.5" /> Travellers
            </Label>
            <Input type="number" min={1} max={20} value={String(form.travellers ?? "")} data-testid="trip-travellers"
              onChange={(e) => set("travellers", e.target.value === "" ? "" : Math.max(1, Number(e.target.value)))} className="rounded-sm text-ink" />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-moss flex items-center gap-1.5 mb-1.5">
              <DoorClosed className="w-3.5 h-3.5" /> Rooms
            </Label>
            <Input type="number" min={1} max={10} value={String(form.rooms ?? "")} data-testid="trip-rooms"
              onChange={(e) => set("rooms", e.target.value === "" ? "" : Math.max(1, Number(e.target.value)))} className="rounded-sm text-ink" />
          </div>

          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-moss flex items-center gap-1.5 mb-1.5">
              <CalendarDays className="w-3.5 h-3.5" /> Preferred days
            </Label>
            <Input type="number" min={2} max={40} value={String(form.duration ?? "")} data-testid="trip-duration"
              onChange={(e) => set("duration", e.target.value === "" ? "" : Number(e.target.value))} className="rounded-sm text-ink" />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-moss flex items-center gap-1.5 mb-1.5">
              <Activity className="w-3.5 h-3.5" /> Fitness level
            </Label>
            <Select value={form.fitness} onValueChange={(v) => set("fitness", v)}>
              <SelectTrigger data-testid="trip-fitness" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FITNESS.filter((f) => f !== "Any").map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label className="font-mono text-xs uppercase tracking-widest text-moss mb-1.5 block">Style</Label>
            <div className="flex gap-2">
              {["Private", "Group"].map((s) => (
                <button
                  key={s} data-testid={`trip-style-${s.toLowerCase()}`}
                  onClick={() => set("style", s)}
                  className={`flex-1 py-2 rounded-sm text-sm font-medium border transition-colors ${
                    form.style === s ? "bg-moss text-white border-moss" : "bg-white border-sand-dark text-ink-secondary hover:border-moss"
                  }`}
                >{s}</button>
              ))}
            </div>
          </div>
        </div>

        <Button
          data-testid="trip-save-btn"
          onClick={() => onSave({
            ...form,
            travellers: Number(form.travellers) || 2,
            rooms: Number(form.rooms) || 1,
            duration: Number(form.duration) || 7,
          })}
          className="w-full bg-terracotta hover:bg-terracotta-hover text-white rounded-sm h-11 font-semibold tracking-wide"
        >
          Start building
        </Button>
      </DialogContent>
    </Dialog>
  );
}
